import { List } from 'immutable';
import get from 'lodash/get';

import {
  ORGANIZATIONS__FACEBOOK_STATUS__INVALID,
  ORGANIZATIONS__FACEBOOK_STATUS__OFF,
  ORGANIZATIONS__FACEBOOK_STATUS__ON,
  ORGANIZATIONS__GIFTBIT_STATUS__EXPIRED,
  ORGANIZATIONS__GIFTBIT_STATUS__ON,
  ORGANIZATIONS__GIFTBIT_STATUS__OFF,
  ORGANIZATIONS__GIFTBIT_STATUS__INVALID,
} from '../constants';
import { makeIsOrgAdminPredicate, selectCurrentUserId } from '../user/selectors.user';
import { selectMembership } from '../memberships/selectors.memberships';

import { makeColumnSort, naturalCompare, parseUnixTimestamp, pipe } from '../../util';

const voidList = new List();

const getGiftbitStatus = (organization) => {
  const giftbitExpiresAt = organization.get('giftbitExpiresAt');
  const giftbitRefreshToken = organization.get('giftbitRefreshToken');
  const giftbitValidAuth = organization.get('giftbitValidAuth');
  const isExpired = parseUnixTimestamp(giftbitExpiresAt) <= Date.now();
  if (!giftbitRefreshToken) return ORGANIZATIONS__GIFTBIT_STATUS__OFF;
  if (giftbitValidAuth === false) return ORGANIZATIONS__GIFTBIT_STATUS__INVALID;
  if (isExpired) return ORGANIZATIONS__GIFTBIT_STATUS__EXPIRED;
  return ORGANIZATIONS__GIFTBIT_STATUS__ON;
};
export const getFacebookStatus = (organization) => {
  if (!organization.get('facebookConnected')) return ORGANIZATIONS__FACEBOOK_STATUS__OFF;
  if (organization.get('facebookValidAuth') === false)
    return ORGANIZATIONS__FACEBOOK_STATUS__INVALID;
  return ORGANIZATIONS__FACEBOOK_STATUS__ON;
};
const makeOrganizationEnhancer = (state) => (organization) => {
  const organizationId = organization.get('id');
  const userId = selectCurrentUserId(state);
  const membership = selectMembership(state, userId, organizationId);
  const facebookStatus = getFacebookStatus(organization);
  const giftbitStatus = getGiftbitStatus(organization);
  const lyticsApiKey = organization.get('lyticsApiKey');
  const lyticsStream = organization.get('lyticsStream');
  const lyticsIsConnected = Boolean(lyticsApiKey && lyticsStream);
  const bigqueryProjectId = organization.get('bigqueryProjectId');
  const bigqueryDataset = organization.get('bigqueryDataset');
  const bigqueryTable = organization.get('bigqueryTable');
  const bigqueryIsConnected = Boolean(bigqueryProjectId && bigqueryDataset && bigqueryTable);
  const twilioData = state.organizations.getIn([`${organizationId}_twilio_data`, 'twilioData']);
  return organization.merge({
    membership,
    facebookStatus,
    giftbitStatus,
    lyticsIsConnected,
    bigqueryIsConnected,
    twilioData,
  });
};
const makeMembershipReplacer = (state, userId) => (organization) => {
  const organizationId = organization.get('id');
  const membership = selectMembership(state, userId, organizationId);
  return organization.merge({ membership });
};
const organizationsListSort = (a, b) => {
  const aName = a.get('name');
  const bName = b.get('name');
  const aRole = a.getIn(['membership', 'role']);
  const bRole = b.getIn(['membership', 'role']);
  if (aRole === bRole) return naturalCompare(aName, bName);
  return naturalCompare(bRole, aRole);
};

const selectUnsortedOrganizations = (state) =>
  state.organizations.filter((item) => !/twilio_data$/.test(item.get('id'))).toList();

export const selectOrganizations = (state) =>
  selectUnsortedOrganizations(state)
    .map(makeOrganizationEnhancer(state))
    .sort(organizationsListSort);

export const selectOrganization = (state, organizationId) => {
  const organization = state.organizations.get(organizationId);
  return organization && makeOrganizationEnhancer(state)(organization);
};
export const selectOrganizationJs = (state, organizationId) => {
  const organization = selectOrganization(state, organizationId);
  return organization && organization.toJS();
};
export const selectOrganizationThemeOptions = (state, organizationId) => {
  const organization = state.organizations.get(organizationId);
  return organization && organization.toJS();
};
export const selectManagedOrganizations = (state, userId) =>
  selectUnsortedOrganizations(state)
    .map(makeOrganizationEnhancer(state))
    .filter(makeIsOrgAdminPredicate(state))
    .map(makeMembershipReplacer(state, userId))
    .sort(makeColumnSort({ sortBy: 'name', sortDirection: 'asc' }));

const getFeatures = (organization) => get(organization, 'features', []);
export const selectOrganizationFeatures = pipe(selectOrganizationJs, getFeatures);

export const organizationHasFeature = (state, organizationId, featureName) =>
  state.organizations
    .getIn([organizationId, 'features'], voidList)
    .findIndex((feature) => feature.get('name') === featureName && feature.get('isActive')) !== -1;
